import { useState } from 'react'

import { Checkbox, LoaderCircle, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import BackIcon from 'mdi-react/ArrowLeftIcon'
import { useNavigate, useParams } from 'react-router-dom'

import { activityResources } from '@/api/activities'
import queries from '@/api/queries'
import { Translation } from '@/components/i18n/Translation'
import { LoadingError } from '@/components/LoadingError'
import { APP_ROUTES } from '@/util/appRoutes'

import { parseActivityOptions } from '../helpers'

export const EndOfLease = () => {
  const queryClient = useQueryClient()

  const [action, setAction] = useState<{
    type: 'return' | 'extend' | 'not_in_my_possession'
    numberOfMonths?: number
  }>()
  const [isPopupOpen, setIsPopupOpen] = useState(false)

  const params = useParams()
  const navigate = useNavigate()

  if (!params.id) {
    navigate(APP_ROUTES.activities)
    return null
  }

  const {
    data: activity,
    isLoading,
    isError,
  } = useQuery(
    queries.activity(Number(params.id)),
    () => activityResources.show(Number(params.id)),
    {
      enabled: Boolean(params.id),
    }
  )

  const { mutate: makeDecisionMutation, isLoading: isMakingDecisionLoading } = useMutation(
    activityResources.makeDecision,
    {
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: queries.profile() })
        // removes individual policy from cache
        queryClient.removeQueries({ queryKey: ['policy'] })
        queryClient.removeQueries({ queryKey: queries.policyList() })
        queryClient.invalidateQueries({ queryKey: queries.activities() })
        queryClient.invalidateQueries({ queryKey: queries.activity(Number(params.id)) })

        setIsPopupOpen(true)
      },
    }
  )

  if (isLoading) return <LoaderCircle />
  if (isError || !activity) return <LoadingError />

  const { asset, activities } = activity

  const activityData = activities[0]

  if (!asset || !activityData) {
    navigate(APP_ROUTES.activities)
    return null
  }

  const options = activityData?.data?.find(data => data.key === 'LeaseExtendOptions')?.value

  if (!options) {
    navigate(APP_ROUTES.activities)
    return null
  }

  const parsedOptions = parseActivityOptions(options)

  const onSubmitHandler = () => {
    if (!params.id) return
    if (!action || !action.type) return

    makeDecisionMutation({
      workflowId: Number(params.id),
      type: action?.type,
      months: action?.numberOfMonths,
    })
  }

  const onConfirmPopup = () => {
    if (action?.type === 'return') {
      if (parsedOptions?.eComPolicyIds?.length > 0) {
        navigate(
          APP_ROUTES.ecomPolicy.replace(':ecomPolicyId', parsedOptions.eComPolicyIds[0].toString())
        )
      } else {
        navigate(APP_ROUTES.ecomPolicyList)
      }
      return
    }

    navigate(APP_ROUTES.activities)
  }

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.activities)}>
      <div className="flex h-full flex-col items-center justify-center">
        <h1 className="mb-4 text-2xl">
          <Translation id="Device" />
        </h1>
        <h3 className="text-lg font-medium text-gray-600">{asset.assetDescription}</h3>
        <p className="m-0 text-base font-light text-gray-600">
          <Translation id="It's time to replace your current device. You can order a new one now or chose to delay the replacement by selecting one of the available options" />
        </p>

        <h1 className="mb-4 text-2xl">
          <Translation id="Make your choice" />
        </h1>

        {/* 4 checkboxes */}
        <div className="flex flex-col">
          {parsedOptions.canReturn && (
            <div className="flex items-center">
              <Checkbox
                className="circle-style"
                label={<Translation id="Hand it in and buy a new device" />}
                id="order"
                checked={action?.type === 'return'}
                onChange={() => setAction({ type: 'return' })}
              />
            </div>
          )}

          {parsedOptions.canSelectNotInMyPossession && (
            <div className="flex items-center">
              <Checkbox
                className="circle-style"
                label={<Translation id="Not in my possession" />}
                id="not-in-possession"
                checked={action?.type === 'not_in_my_possession'}
                onChange={() => setAction({ type: 'not_in_my_possession' })}
              />
            </div>
          )}
          {parsedOptions.options.map(option => (
            <div className="flex items-center" key={option.optionId}>
              <Checkbox
                className="circle-style"
                label={
                  <Translation
                    id="Postpone the submission by {numberOfMonths} month"
                    values={{ numberOfMonths: option.numberOfMonths }}
                  />
                }
                id={'delay' + option.numberOfMonths}
                checked={
                  action?.type === 'extend' && action?.numberOfMonths === option.numberOfMonths
                }
                onChange={() =>
                  setAction({ type: 'extend', numberOfMonths: option.numberOfMonths })
                }
              />
            </div>
          ))}
        </div>

        {/* Button back to activity list in the right and send on the left of the page */}
        <div className="mt-4 flex w-full justify-between">
          <Button
            onClick={() => navigate(APP_ROUTES.activities)}
            disabled={isMakingDecisionLoading}
            Icon={<BackIcon />}
          >
            <Translation id="Back" />
          </Button>
          <Button
            variant="primary"
            onClick={onSubmitHandler}
            disabled={!action || !action.type || isMakingDecisionLoading}
          >
            <Translation id="Submit" />
          </Button>
        </div>
      </div>
      <Popup ariaLabel="test" isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="flex h-full flex-col items-center justify-center">
          <p className="m-0 text-lg font-light text-gray-600">
            {action?.type === 'return' && (
              <Translation id="You have selected hand in your device. We will re-direct you to the shop for purchase!" />
            )}

            {action?.type === 'not_in_my_possession' && (
              <Translation id="You have selected not in possession. IT Manager will contact you with the next steps" />
            )}

            {action?.type === 'extend' && (
              <Translation
                id="You have postponed hand in process. We will remind you in {count} month"
                values={{
                  count: action?.numberOfMonths,
                }}
              />
            )}
          </p>
          <div className="mt-4 flex w-full justify-center">
            <Button onClick={() => onConfirmPopup()} variant="primary" className="w-full">
              <Translation id="Close" />
            </Button>
          </div>
        </div>
      </Popup>
    </Container>
  )
}
